import { addDays, isFuture as isFutureDate, isPast as isPastDate, startOfWeek } from "date-fns";
import { maxBy } from "lodash-es";

import type { ContractSchema } from "@asap/shared/schemas/contract/list";
import type { ContractTableRow } from "@asap/shared/types";
import type { PlanningStatus } from "@asap/shared/types/planning";
import { EXTENSION_AMENDMENT_MAX_ALLOWED } from "@asap/shared/utils/constants";
import { dateFormat } from "@asap/shared/utils/date";
import { translateDefinitiveEndType } from "@asap/shared/utils/translate";
import type { ContractStatus } from "@asap/shared/utils/supabase.enum";
import type { ContractForTable } from "@asap/shared/utils/supabase.types";

type PlanningStatusContract = Pick<
  ContractForTable,
  "start_date" | "end_date" | "real_end_date" | "is_definitive_end" | "definitive_end_type"
> & { previous_contract_key?: number | null };

export const getPlanningStatus = (
  contract: PlanningStatusContract,
  withUpcomingDetails: boolean = true
): PlanningStatus => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const {
    start_date: startDateDB,
    end_date: endDateDB,
    real_end_date: realEndDateDB,
    is_definitive_end: isDefinitiveEnd,
    previous_contract_key: previousContractKey,
    definitive_end_type: definitiveEndType,
  } = contract;

  // We set hours to 0 to compare only the date, we don't care about the time
  const startDate = new Date(startDateDB!);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(realEndDateDB ?? endDateDB!);
  endDate.setHours(0, 0, 0, 0);

  // Current week - get the first day of current week and the last day of current week
  const firstDayOfCurrentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
  const endDayOfCurrentWeek = addDays(firstDayOfCurrentWeek, 6);

  // Next week - get the first day of next week and the last day of next week
  const firstDayOfNextWeek = startOfWeek(addDays(new Date(), 7), { weekStartsOn: 1 });
  const endDayOfNextWeek = addDays(firstDayOfNextWeek, 6);

  // Get the next monday from the end date
  const endDayOfWeek = endDate.getDay();
  const daysUntilNextMonday = endDayOfWeek === 0 ? 1 : endDayOfWeek === 1 ? 7 : 8 - endDayOfWeek;
  const nextMondayOfEndDate = new Date(endDate);
  nextMondayOfEndDate.setDate(endDate.getDate() + daysUntilNextMonday);

  if ((isDefinitiveEnd && endDate < now) || definitiveEndType === "contract_renewal") return "done"; // Contract is done and definitive end is set = done
  if (
    nextMondayOfEndDate > now &&
    endDate >= firstDayOfCurrentWeek &&
    endDate <= nextMondayOfEndDate &&
    endDate <= endDayOfCurrentWeek
  )
    return "almost_done"; // Contract will end this week and before next monday = almost_done // Contract is a renewal and has not been paused so it is currently ongoing
  if (previousContractKey && (startDate >= firstDayOfNextWeek || startDate >= now) && endDate > now) return "current";
  if (startDate >= firstDayOfNextWeek && startDate <= endDayOfNextWeek)
    return withUpcomingDetails ? "upcoming_next_week" : "upcoming"; // Contract start next week = start-next-week
  if (startDate >= firstDayOfCurrentWeek && startDate <= endDayOfCurrentWeek && startDate > now)
    return withUpcomingDetails ? "upcoming_this_week" : "upcoming"; // Contract start this week = start-this-week

  if (startDate <= now && nextMondayOfEndDate > now) return "current"; // Contract is ongoing = current
  if (startDate > now && endDate > now) return "upcoming"; // Contract will start but in some time = upcoming
  if (!isDefinitiveEnd && nextMondayOfEndDate <= now) return "late"; // Contract is done but definitive end is not set = late
  return "done"; // Contract is done and definitive end is set = done
};

export const getPlanningStatusV2 = (
  contract: PlanningStatusContract & {
    planning_status?: "done" | "current" | "upcoming" | "late" | "almost_done";
  },
  amendments: ContractTableRow[],
  withUpcomingDetails: boolean = true,
  renewals: ContractTableRow[] = []
): PlanningStatus => {
  const now = new Date();
  const startDate = new Date(contract.start_date!);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(contract.real_end_date ?? contract.end_date!);
  endDate.setHours(23, 59, 59, 999);

  const firstDayOfCurrentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
  const endDayOfCurrentWeek = addDays(firstDayOfCurrentWeek, 6);
  endDayOfCurrentWeek.setHours(23, 59, 59, 999);

  if (
    amendments.some(
      (amendment) =>
        !["draft", "cancelled", "refused"].includes(amendment.status) && new Date(amendment.start_date!)! > now
    )
  ) {
    return "current";
  }

  if (renewals.some((renewal) => !["draft", "cancelled"].includes(renewal.status))) {
    return "done";
  }

  if (contract.definitive_end_type === "contract_renewal") {
    return "done";
  }

  if (contract.planning_status) {
    if (contract.planning_status === "upcoming" && endDate > now && endDate <= endDayOfCurrentWeek) {
      return "almost_done";
    }
    if (contract.planning_status === "upcoming" && withUpcomingDetails) {
      return getPlanningStatus(contract, true);
    }
    return contract.planning_status;
  }

  const nowAtMidnight = new Date(now);
  nowAtMidnight.setHours(0, 0, 0, 0);
  const firstDayOfNextWeek = startOfWeek(addDays(new Date(), 7), { weekStartsOn: 1 });

  if (
    contract.previous_contract_key &&
    (startDate >= firstDayOfNextWeek || startDate >= nowAtMidnight) &&
    endDate > nowAtMidnight
  ) {
    return "current";
  }

  if (startDate < now && endDate > now && endDate <= endDayOfCurrentWeek) {
    return "almost_done";
  }
  if (startDate < now && endDate > now) return "current";
  if (endDate < now) {
    if (
      contract.is_definitive_end ||
      (contract.definitive_end_type &&
        ["contract_renewal", "contract_new", "contract_new_with_different_client"].includes(
          contract.definitive_end_type
        ))
    ) {
      return "done";
    }
    return "late";
  }

  return getPlanningStatus(contract, withUpcomingDetails);
};

// ========================================
// Extension Amendment Business Rules
// ========================================
// In France, temporary work contracts extensions ("prolongations") are limited to 2 per contract,
// while modifications are unlimited (as long as dates remain the same)

export const EXTENSION_AMENDMENT_RULES = {
  MAX_ALLOWED: EXTENSION_AMENDMENT_MAX_ALLOWED,
  MESSAGES: {
    MAX_REACHED: `${EXTENSION_AMENDMENT_MAX_ALLOWED} prolongations existent déjà sur ce contrat, tu dois créer un renouvellement !`,
    CONTRACT_REFUSED: "Ce contrat a été refusé et ne peut pas être amendé.",
    CONTRACT_NOT_SIGNED: "Le contrat doit être signé pour créer un avenant de prolongation.",
    DEFINITIVE_END: "Ce contrat a une fin définitive. Supprimez-la pour pouvoir créer un avenant.",
    NO_CONTRACT_FOUND:
      "Aucun contrat trouvé pour cet intérimaire et ce client. Un contrat initial doit exister dans PLD avant de pouvoir créer un avenant.",
    DATE_MISMATCH_WARNING:
      "La date du début de la prolongation devrait être (J+1) de la date de fin du dernier contrat. Vérifiez que vous avez bien sélectionné le bon contrat.",
    END_DATE_REQUIRED: "La date de fin est requise",
  },
} as const;

/**
 * Check if the maximum number of extensions has been reached
 * @param extensionCount Current number of valid extension amendments
 * @returns true if maximum extensions reached
 */
export const hasReachedMaxExtensions = (extensionCount: number): boolean => {
  return extensionCount >= EXTENSION_AMENDMENT_RULES.MAX_ALLOWED;
};

/**
 * Get the reason why an extension cannot be created (if applicable)
 * @param extensionCount Current number of extension amendments
 * @param status Contract status
 * @param isDefinitiveEnd Whether contract has definitive end (boolean | null)
 * @returns Error message or null if extension can be created
 */
export const getExtensionBlockedReason = (
  extensionCount: number,
  status: ContractStatus,
  isDefinitiveEnd: boolean | null
): string | null => {
  if (isDefinitiveEnd) {
    return EXTENSION_AMENDMENT_RULES.MESSAGES.DEFINITIVE_END;
  }

  if (status === "refused") {
    return EXTENSION_AMENDMENT_RULES.MESSAGES.CONTRACT_REFUSED;
  }

  if (status !== "signed") {
    return EXTENSION_AMENDMENT_RULES.MESSAGES.CONTRACT_NOT_SIGNED;
  }

  if (hasReachedMaxExtensions(extensionCount)) {
    return EXTENSION_AMENDMENT_RULES.MESSAGES.MAX_REACHED;
  }

  return null;
};

// ****************
// Date Comparisons
// ****************

type ContractWithDates = {
  start_date?: string | null;
  end_date?: string | null;
  real_end_date?: string | null;
};

export const isCurrent = function (contract: ContractWithDates) {
  if (!contract.start_date) return false;
  if (!contract.end_date && !contract.real_end_date) return false;

  const isStartPast = isPastDate(new Date(contract.start_date));
  const isEndFuture = contract.end_date ? isFutureDate(new Date(contract.end_date)) : false;
  const isRealEndFuture = contract.real_end_date ? isFutureDate(new Date(contract.real_end_date)) : false;

  return isStartPast && (isEndFuture || isRealEndFuture);
};

export const isFuture = function (contract: ContractWithDates) {
  if (!contract.start_date) return false;

  const isStartFuture = isFutureDate(new Date(contract.start_date));

  return isStartFuture;
};

export const isPast = function (contract: ContractWithDates) {
  if (!contract.end_date && !contract.real_end_date) return false;

  const isEndPast = contract.end_date ? isPastDate(new Date(contract.end_date)) : false;
  const isRealEndPast = contract.real_end_date ? isPastDate(new Date(contract.real_end_date)) : false;

  return isEndPast || isRealEndPast;
};

export const latestVersion = function (contracts: { created_at: string; contract_key: number }[]) {
  if (!contracts.length) return [];

  // Assert all contracts have the same contract key
  if (!contracts.every((contract) => contract.contract_key === contracts[0].contract_key))
    throw new Error("All `contract_key` should be identical.");

  return maxBy(contracts, (contract) => contract.created_at);
};

/** Contract statuses listed in the planning. */
export const PLANNING_CONTRACT_STATUSES: ContractStatus[] = [
  "to_process",
  "to_send",
  "to_modify_by_owner",
  "to_modify_by_admin",
  "to_validate",
  "waiting_for_signature",
  "refused",
  "signed",
];

/** End date shown in the planning: the latest valid extension wins, its real end date first. */
export const getPlanningContractEndDates = (
  contract: { real_end_date: string | null; end_date: string | null },
  amendments: ContractTableRow[]
): { date: string | undefined; subDate: string | undefined } => {
  const latestAmendment = amendments
    .filter(
      (amendment) => amendment.amendment_type === "extension" && !["draft", "cancelled"].includes(amendment.status)
    )
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .at(0);
  const currentContract = latestAmendment || contract;
  const realEndDate = currentContract.real_end_date || currentContract.end_date || undefined;
  const subEndDate =
    currentContract.real_end_date && currentContract.real_end_date !== currentContract.end_date
      ? currentContract.end_date
      : undefined;
  return {
    date: realEndDate,
    subDate: subEndDate || undefined,
  };
};

const formatPlanningDate = (date: string | null | undefined) =>
  date ? dateFormat(date, { options: { dateStyle: "medium" } }) : "non définie";

export const getPlanningStatusDescription = ({
  planningStatus,
  contract,
  amendments,
  renewals,
  initialContract,
}: {
  planningStatus: PlanningStatus;
  contract: { start_date: string | null; real_end_date: string | null; end_date: string | null };
  amendments: ContractTableRow[];
  renewals: ContractTableRow[];
  initialContract?: ContractTableRow;
}): string => {
  const startDate = formatPlanningDate(initialContract?.start_date ?? contract.start_date);
  const endDate = formatPlanningDate(getPlanningContractEndDates(contract, amendments).date);

  switch (planningStatus) {
    case "late":
      return `La date de fin (${endDate}) est dépassée et aucune fin définitive n'a été définie`;
    case "almost_done":
      return `La mission se termine le ${endDate}`;
    case "current":
      return `La mission est en cours jusqu'au ${endDate}`;
    case "upcoming":
    case "upcoming_next_week":
    case "upcoming_this_week":
      return `La mission commence le ${startDate}`;
    case "done": {
      const renewal = renewals.at(0);
      if (renewal) {
        return `La mission est renouvelée par le contrat #${renewal.contract_key}, qui débute le ${formatPlanningDate(renewal.start_date)}`;
      }
      return `La mission s'est terminée le ${endDate}`;
    }
  }
};

const getDefinitiveEndDescription = (contract: Pick<ContractSchema, "definitive_end_type">): string => {
  const definitiveEndType = contract.definitive_end_type;
  if (!definitiveEndType) return "Date de fin non définie";
  return translateDefinitiveEndType.fr[definitiveEndType];
};

/** The status description, completed with the kind of definitive end when there is one. */
export const getPlanningStatusDetailedDescription = (params: {
  planningStatus: PlanningStatus;
  contract: Pick<
    ContractSchema,
    "start_date" | "real_end_date" | "end_date" | "is_definitive_end" | "definitive_end_type"
  >;
  amendments: ContractTableRow[];
  renewals: ContractTableRow[];
  initialContract?: ContractTableRow;
}): string => {
  const description = getPlanningStatusDescription(params);
  // A renewal is already described
  if (!params.contract.is_definitive_end || params.renewals.length > 0) return description;
  return `${description} (${getDefinitiveEndDescription(params.contract)})`;
};
